import { get } from 'svelte/store';
import {
  activeMatch,
  currentScreen,
  fullScreenBoard,
  fullScreenBoardStartedAt,
  isRecording,
  lastBallState,
  peerConnected
} from '../svelte/store.js';
import { appSettings } from '../store.js';
import { PeerSync } from '../peer-sync.js';
import { go } from '../router.js';
import { attachRemoteAudioStream, playRemoteSfx } from '../broadcast/broadcast-service.js';
import { triggerAnimation } from '../broadcast/overlay-state.js';
import {
  broadcastRecordingStatus,
  startRecording,
  stopRecording,
  togglePauseRecording
} from '../media/recording-service.js';

export { isNewerPeerSnapshot } from './snapshot-order.js';
export { sendCurrentMatchState } from './state-publisher.js';

function buildRemoteMatch(data) {
  if (!data?.teamA || !data?.teamB) return null;
  return {
    id: data.id || `remote_${Date.now()}`,
    date: data.date || new Date().toISOString(),
    teamA: { name: data.teamA.name, players: data.teamA.players || [] },
    teamB: { name: data.teamB.name, players: data.teamB.players || [] },
    overs: data.overs || 20,
    mode: data.mode || 'full',
    lms: !!data.lms,
    toss: data.toss || {},
    innings: data.innings || [],
    result: data.result || null,
    _stateRevision: Number(data.revision ?? data._stateRevision) || 0,
    _introPlayed: true,
    _introPlayedAt: data._introPlayedAt || null,
    _remoteControlled: true
  };
}

function updateRemoteMatch(match, data) {
  if (!match || !data) return match;
  match.teamA = { name: data.teamA.name, players: data.teamA.players || match.teamA.players };
  match.teamB = { name: data.teamB.name, players: data.teamB.players || match.teamB.players };
  match.overs = data.overs || match.overs;
  match.mode = data.mode || match.mode;
  match.lms = !!data.lms;
  match.toss = data.toss || match.toss;
  match.innings = data.innings || match.innings;
  if (data.result != null) match.result = data.result;
  match._stateRevision = Number(data.revision ?? data._stateRevision) || match._stateRevision || 0;
  match._introPlayedAt = data._introPlayedAt || match._introPlayedAt || null;
  match._remoteControlled = true;
  return match;
}

export function setupRecorderPeerHandlers() {
  PeerSync.onState((data) => {
    const current = get(activeMatch);
    let next;
    if (!current || !current._remoteControlled) {
      next = buildRemoteMatch(data);
    } else if (current.id !== data.id) {
      // Different match — full rebuild instead of merge
      next = buildRemoteMatch(data);
    } else {
      next = updateRemoteMatch(current, data);
    }
    if (!next) return;
    activeMatch.set(next);

    // Mirror the scorer's ball result + commentary so the recorder's burnt-in
    // overlay can show it. Only apply a NEWER one, or a stale packet would
    // resurrect a ball banner that already faded out.
    if (data.lastBall?.ts && Number(data.lastBall.ts) > (get(lastBallState)?.ts || 0)) {
      lastBallState.set(data.lastBall);
    }

    const previousBoard = get(fullScreenBoard);
    const nextBoard = data.fullScreenBoard || null;
    if (previousBoard !== nextBoard) {
      fullScreenBoard.set(nextBoard);
      fullScreenBoardStartedAt.set(Date.now());
    }

    if (data.result && !nextBoard && get(currentScreen) !== 'result') {
      if (get(isRecording)) stopRecording();
      go('result');
    }
  });

  PeerSync.onAnim((animation) => {
    if (appSettings.eventAnimationsEnabled === false) return;
    try { triggerAnimation(animation, { forward: false }); }
    catch (err) { console.warn('[Recorder] animation failed:', err); }
  });

  PeerSync.onSfx((payload) => {
    try { playRemoteSfx(payload?.fx || payload); }
    catch (err) { console.warn('[Recorder] sound failed:', err); }
  });

  PeerSync.onRecCmd((command) => {
    const action = command?.action;
    try {
      if (action === 'start' && !get(isRecording)) startRecording();
      else if (action === 'pause' && get(isRecording)) togglePauseRecording();
      else if (action === 'stop' && get(isRecording)) stopRecording();
    } catch (err) {
      console.warn('[Recorder] remote recording command failed:', err);
    }
    setTimeout(broadcastRecordingStatus, 150);
  });

  PeerSync.onDisconnected(() => peerConnected.set(false));
  PeerSync.onConnected(() => {
    peerConnected.set(true);
    broadcastRecordingStatus();
  });

  PeerSync.onAudioStream((remoteStream) => {
    try { attachRemoteAudioStream(remoteStream, appSettings.scorerMicGain); }
    catch (err) { console.warn('[Recorder] commentary mix failed:', err); }
  });
}
